import { ContentLayout } from 'layouts/ContentLayout/ContentLayout';
import React, { useEffect } from 'react';
import CustomRoleHeader from './CustomRoleHeader';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getCustomRoleById, putCustomRole } from '../utils';
import Loader from 'components/Loader';
import { FormProvider, useForm } from 'react-hook-form';
import { CustomRoleFormType } from './custom-roles.types';
import { CustomRoleSchema } from './Form/schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { getCustomRolePermissions, transformPermissions } from './utils';
import showToast from 'components/Toast/Toast';
import Form from './Form/Form';

const EditCustomRole = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery(
    [`custom-role-edit`, id],
    () => getCustomRoleById(id as string),
    { refetchOnMount: true },
  );

  const { reset, control, setValue, ...methods } = useForm<CustomRoleFormType>({
    resolver: zodResolver(CustomRoleSchema),
  });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        description: data.description || '',
        color: data.color || '',
        permissions: transformPermissions(data.permissions.map((p) => p.name)),
      });
    }
  }, [data, reset]);

  const queryClient = useQueryClient();

  const { mutate, isLoading: putLoading } = useMutation(putCustomRole, {
    onSuccess: (data) => {
      showToast.confirmation({
        title: 'Role updated!',
        message: `${data?.name}`,
      });
      queryClient.refetchQueries('custom-roles-index-table');
      queryClient.refetchQueries([`custom-role-edit`, id]);
      queryClient.refetchQueries([`custom-role-details`, id]);
      navigate('..', { replace: true });
    },
  });

  const onSubmit = (data: CustomRoleFormType) =>
    id &&
    mutate({
      id,
      ...data,
      permissions: getCustomRolePermissions(data.permissions ?? []),
    });

  if (isLoading) return <Loader className="flex-1" />;
  if (data)
    return (
      <ContentLayout className="min-h-screen !justify-start">
        <FormProvider
          {...methods}
          reset={reset}
          control={control}
          setValue={setValue}
        >
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-1 flex-col"
          >
            <CustomRoleHeader
              title={data.name}
              breadcrumbs={[{ label: data.name }]}
              isDirty={methods.formState.isDirty}
              withUnsavedChangeModal={true}
              buttonAction={{
                children: 'Save changes',
                disabled: putLoading,
                type: 'submit',
                size: 'large',
                className: 'md:w-max w-full',
              }}
            />
            <Form withDeleteButton={true} />
          </form>
        </FormProvider>
        <Outlet />
      </ContentLayout>
    );
};

export default EditCustomRole;
